import { useState, useRef, forwardRef } from 'react';
import { FileChartLine, ChevronDown, Download, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getAuthToken } from '../../utils/auth';
import { formatDate, DATE_FORMATS } from '../../utils/dateFormatter';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

const ReportCard = forwardRef(function ReportCard({ report }, ref) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const iframeRef = useRef(null);

  const formatReportDate = (dateString) => {
    if (!dateString) return '';
    return formatDate(dateString, { ...DATE_FORMATS.shortDayMonth, weekday: 'short', year: 'numeric' });
  };

  const getReportTypeLabel = (type) => {
    return type === 'midterm' ? t('report.midterm') : t('report.final');
  };

  const getReportTypeColor = (type) => {
    return type === 'midterm'
      ? 'bg-[#589DD5] dark:bg-transparent dark:border dark:border-[#589DD5]'
      : 'bg-[#4a8bc4] dark:bg-transparent dark:border dark:border-[#4a8bc4]';
  };

  const handleIframeLoad = () => {
    if (iframeRef.current) {
      try {
        const iframeDoc = iframeRef.current.contentWindow.document;
        const containerWidth = iframeRef.current.parentElement.offsetWidth;
        const contentWidth = Math.max(
          iframeDoc.body.scrollWidth,
          iframeDoc.documentElement.scrollWidth
        );
        const contentHeight = Math.max(
          iframeDoc.body.scrollHeight,
          iframeDoc.documentElement.scrollHeight
        );

        // Scale to fit container width
        const scale = Math.min(1, containerWidth / contentWidth);
        const scaledWidth = contentWidth * scale;
        const leftOffset = (containerWidth - scaledWidth) / 2;

        // Set iframe to content's natural size, then scale the iframe itself
        iframeRef.current.style.width = `${contentWidth}px`;
        iframeRef.current.style.height = `${contentHeight}px`;
        iframeRef.current.style.transform = `translateX(${leftOffset}px) scale(${scale})`;
        iframeRef.current.style.transformOrigin = 'top left';

        // Adjust container height to match scaled content
        iframeRef.current.parentElement.style.height = `${contentHeight * scale}px`;
      } catch (e) {
        console.error('Error resizing iframe:', e);
      }
    }
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    if (!report.uuid || isDownloading) return;

    setIsDownloading(true);
    try {
      const token = getAuthToken();
      // Open PDF in new tab - let browser handle viewing/downloading
      window.open(`${API_URL}/api/v1/parent/reports/${report.uuid}/pdf?token=${encodeURIComponent(token)}`, '_blank');
    } catch (err) {
      console.error('Error downloading PDF:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div
      ref={ref}
      className="bg-white dark:bg-gray-800 rounded-xl mb-3 sm:mb-4 shadow-sm hover:shadow-md dark:shadow-gray-900/30 transition-shadow duration-200 overflow-hidden animate-fade-in border border-gray-200 dark:border-gray-700"
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full text-left p-3 sm:p-5"
      >
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-2.5 sm:gap-4 min-w-0 flex-1">
            <div className={`flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center ${getReportTypeColor(report.report_type)} text-white dark:text-[#589DD5]`}>
              <FileChartLine className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={1.5} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base truncate">
                {getReportTypeLabel(report.report_type)}
              </h3>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {formatReportDate(report.first_published_at)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <div
              onClick={handleDownload}
              className={`flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-colors ${
                isDownloading ? 'cursor-wait' : 'hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer'
              }`}
            >
              {isDownloading ? (
                <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 dark:text-gray-500 animate-spin" />
              ) : (
                <Download className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 dark:text-gray-500" />
              )}
            </div>
            <div className={`flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-colors ${
              isOpen ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}>
              <ChevronDown
                className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-400 dark:text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
              />
            </div>
          </div>
        </div>
      </button>

      {/* Expanded Content - Report HTML Preview with smooth animation */}
      <div className="accordion-wrapper" data-open={isOpen}>
        <div className="accordion-content">
          <div className="border-t border-gray-100 dark:border-gray-700">
            {report.report_html ? (
              <div className="overflow-hidden">
                <iframe
                  ref={iframeRef}
                  srcDoc={report.report_html}
                  onLoad={handleIframeLoad}
                  style={{
                    border: 'none',
                    backgroundColor: '#fff',
                    display: 'block'
                  }}
                  title={`${getReportTypeLabel(report.report_type)} Preview`}
                />
              </div>
            ) : (
              <div className="bg-gray-50 dark:bg-gray-900 px-3 py-6 sm:px-5 sm:py-8 text-center">
                <p className="text-xs sm:text-sm text-gray-400 dark:text-gray-500">{t('report.previewUnavailable')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default ReportCard;
